import { FC, useState, useEffect, FormEvent } from 'react';
import { adminService } from '@/services/admin.service';
import type { BroadcastLog } from '@/types/admin';

const AdminBroadcastPage: FC = () => {
  // Form state
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [actionUrl, setActionUrl] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ count: number; message: string } | null>(null);
  const [sendError, setSendError] = useState('');

  // Confirmation modal
  const [showConfirm, setShowConfirm] = useState(false);
  const [activeUsersCount, setActiveUsersCount] = useState<number | null>(null);

  // History state
  const [history, setHistory] = useState<BroadcastLog[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const data = await adminService.getBroadcastHistory();
      setHistory(data);
    } catch {
      // silently fail
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handlePreSend = async (e: FormEvent) => {
    e.preventDefault();
    setSendError('');
    setSendResult(null);

    if (!title.trim() || !message.trim()) {
      setSendError('Título e mensagem são obrigatórios');
      return;
    }

    try {
      const count = await adminService.getActiveUsersCount();
      setActiveUsersCount(count);
      setShowConfirm(true);
    } catch {
      setSendError('Erro ao verificar usuários ativos');
    }
  };

  const handleConfirmSend = async () => {
    setShowConfirm(false);
    setIsSending(true);
    setSendError('');

    try {
      const result = await adminService.sendBroadcast({
        title: title.trim(),
        message: message.trim(),
        action_url: actionUrl.trim() || undefined,
      });
      setSendResult(result);
      setTitle('');
      setMessage('');
      setActionUrl('');
      loadHistory();
    } catch {
      setSendError('Erro ao enviar notificação');
    } finally {
      setIsSending(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-content-primary mb-8">Notificações Broadcast</h1>

      {/* Send Form */}
      <div className="bg-surface-secondary rounded-2xl border border-white/10 p-6 mb-8">
        <h2 className="text-lg font-semibold text-content-primary mb-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-orange-400">campaign</span>
          Enviar Notificação
        </h2>

        <form onSubmit={handlePreSend} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">
              Título <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Ex: Nova funcionalidade disponível!"
              className="w-full px-4 py-2 bg-surface-primary border border-white/10 rounded-lg text-content-primary placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-orange-500/50"
            />
            <span className="text-xs text-content-muted mt-1 block">{title.length}/200</span>
          </div>

          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">
              Mensagem <span className="text-red-400">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Escreva a mensagem da notificação..."
              className="w-full px-4 py-2 bg-surface-primary border border-white/10 rounded-lg text-content-primary placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-orange-500/50 resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-content-secondary mb-1">
              URL de Ação (opcional)
            </label>
            <input
              type="text"
              value={actionUrl}
              onChange={(e) => setActionUrl(e.target.value)}
              placeholder="Ex: /trilhas ou https://exemplo.com"
              className="w-full px-4 py-2 bg-surface-primary border border-white/10 rounded-lg text-content-primary placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-orange-500/50"
            />
          </div>

          {/* Preview */}
          {(title.trim() || message.trim()) && (
            <div className="border border-white/10 rounded-lg p-4 bg-surface-primary">
              <p className="text-xs text-content-muted mb-2 uppercase tracking-wider font-semibold">Preview</p>
              <div className="flex items-start gap-3">
                <div className="bg-blue-500/20 p-2 rounded-lg shrink-0">
                  <span className="material-symbols-outlined text-blue-400 text-xl">campaign</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-content-primary">{title || 'Título da notificação'}</p>
                  <p className="text-sm text-content-secondary mt-0.5">{message || 'Mensagem da notificação'}</p>
                  {actionUrl && (
                    <p className="text-xs text-blue-400 mt-1">{actionUrl}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {sendError && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
              {sendError}
            </div>
          )}

          {sendResult && (
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-green-400 text-sm">
              {sendResult.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSending || !title.trim() || !message.trim()}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSending ? (
              <>
                <span className="material-symbols-outlined animate-spin text-lg">progress_activity</span>
                Enviando...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-lg">send</span>
                Enviar Broadcast
              </>
            )}
          </button>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface-secondary rounded-2xl border border-white/10 p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-content-primary mb-2">Confirmar envio</h3>
            <p className="text-content-secondary mb-4">
              Você está prestes a enviar esta notificação para{' '}
              <span className="font-bold text-orange-400">{activeUsersCount}</span> usuários ativos.
            </p>
            <div className="border border-white/10 rounded-lg p-3 mb-4 bg-surface-primary">
              <p className="text-sm font-semibold text-content-primary">{title}</p>
              <p className="text-sm text-content-secondary mt-1">{message}</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 border border-white/10 rounded-lg text-content-secondary hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmSend}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
              >
                Confirmar Envio
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-surface-secondary rounded-2xl border border-white/10 p-6">
        <h2 className="text-lg font-semibold text-content-primary mb-4 flex items-center gap-2">
          <span className="material-symbols-outlined text-orange-400">history</span>
          Histórico de Broadcasts
        </h2>

        {isLoadingHistory ? (
          <div className="flex justify-center py-8">
            <span className="material-symbols-outlined animate-spin text-3xl text-content-muted">progress_activity</span>
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-content-muted">
            <span className="material-symbols-outlined text-4xl mb-2 block">notifications_off</span>
            Nenhum broadcast enviado ainda
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-content-muted text-left">
                  <th className="pb-3 font-medium">Data</th>
                  <th className="pb-3 font-medium">Admin</th>
                  <th className="pb-3 font-medium">Título</th>
                  <th className="pb-3 font-medium">Mensagem</th>
                  <th className="pb-3 font-medium text-right">Destinatários</th>
                </tr>
              </thead>
              <tbody>
                {history.map((log) => (
                  <tr key={log.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="py-3 pr-4 text-content-secondary whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="py-3 pr-4 text-content-primary whitespace-nowrap">
                      {log.admin_name}
                    </td>
                    <td className="py-3 pr-4 text-content-primary font-medium max-w-[200px] truncate">
                      {log.title}
                    </td>
                    <td className="py-3 pr-4 text-content-secondary max-w-[300px] truncate">
                      {log.message}
                    </td>
                    <td className="py-3 text-right">
                      <span className="bg-orange-500/10 text-orange-400 px-2 py-1 rounded-full text-xs font-semibold">
                        {log.recipients_count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBroadcastPage;
